import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Ticket, Users, BarChart3, ChevronRight } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Bienvenido a NextPlan');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex flex-1">
        {/* Left side - Hero */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="relative z-10 flex flex-col justify-center p-12 w-full">
            <div className="space-y-8">
              {/* Logo */}
              <div className="flex items-center gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_velocity-ops-1/artifacts/3sjrhvi5_Logo%20NextPlan.png"
                  alt="NextPlan Logo"
                  className="h-16 object-contain"
                />
              </div>

              <p className="text-xl text-gray-600 max-w-md">
                Centro Operacional de Cumplimiento y Productividad
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <Ticket className="w-8 h-8 text-[#7FD02E] mb-3" />
                  <h3 className="font-semibold text-gray-800">Gestión de Tickets</h3>
                  <p className="text-sm text-gray-500 mt-1">Control completo del ciclo de vida</p>
                </div>
                <div className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <Users className="w-8 h-8 text-[#4A4A4A] mb-3" />
                  <h3 className="font-semibold text-gray-800">MultiClientes</h3>
                  <p className="text-sm text-gray-500 mt-1">Gestiona actividades de tus clientes de manera ordenada</p>
                </div>
                <div className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <BarChart3 className="w-8 h-8 text-[#7FD02E] mb-3" />
                  <h3 className="font-semibold text-gray-800">KPIs y Métricas</h3>
                  <p className="text-sm text-gray-500 mt-1">Trazabilidad y cumplimiento SLA</p>
                </div>
                <div className="p-5 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <ChevronRight className="w-8 h-8 text-[#4A4A4A] mb-3" />
                  <h3 className="font-semibold text-gray-800">Planificación</h3>
                  <p className="text-sm text-gray-500 mt-1">Vista Kanban y calendario</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_velocity-ops-1/artifacts/3sjrhvi5_Logo%20NextPlan.png"
                alt="NextPlan Logo"
                className="h-10 object-contain"
              />
            </div>

            <Card className="border-gray-200 bg-white shadow-lg" data-testid="auth-card">
              <CardHeader className="text-center">
                <CardTitle className="font-heading text-2xl text-gray-800">Bienvenido</CardTitle>
                <CardDescription className="text-gray-500">Ingresa a tu cuenta</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-gray-700">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-gray-700">Contraseña</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#7FD02E] hover:bg-[#6BC024] text-white"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Ingresar
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="py-4 text-center text-sm text-gray-400 border-t border-gray-100">
        © NextPlan {new Date().getFullYear()}
      </footer>
    </div>
  );
}
